import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DrillSuggest } from './Drills';
import { getToken } from '../lib/api';
import { axisName, useT } from '../lib/i18n';
import { alert, haptic } from '../lib/telegram';
import { fmtTime } from '../lib/time';
import { GrowArea } from './GrowArea';
import { PinnedSheet } from './SetSheet';
import { VisLabel } from './Vis';
import { fetchSeatState, RemoveSheet, removeRefusal } from './RemoveSheet';
import { LiveHeader } from './LiveHeader';
import { groupSummary } from '../lib/today-card';
import { browserStore, dropLocal, groupKey, keepLocal, lastScores, readLocal, restoreFrom, takeLast } from '../lib/live-lesson';

/**
 * One notes screen for a session with two or more players (server:
 * coach/session-notes.ts). A session with one player keeps the rating
 * sheet.
 *
 * On top, shared by everyone: «Что делали» (the plan, with suggestions
 * from the exercise library) and «Заметка для всех». Below, the players
 * as rows; tapping one opens it — ratings, «Лично для …», homework — and
 * closes the one that was open. A row with anything in it shows ✓.
 *
 * Kept as a draft while the coach types (during the lesson too), so it
 * can be closed and reopened any time. «Сохранить» writes everything for
 * every player in one request, all or nothing; when it fails nothing is
 * lost. Each player gets the same message as before: the note for
 * everyone, their own note and their homework. Saved before the lesson
 * has ended, they see it at the end time.
 *
 * Each skill shows the player's last score as «в прошлый раз N»; one tap
 * takes it, and untouched skills stay at 0, not trained today. Changes
 * not yet on the server are also kept in this browser, so a Telegram
 * killed offline loses nothing. Opened from the Today card during the
 * lesson, `live` puts the lesson's header on top.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw Object.assign(new Error(b.error ?? `http_${res.status}`), { body: b });
  }
  return res.json();
}

interface PlayerDraft { scores: Record<string, number>; note: string; homework: string; workingOn: string }
interface Draft {
  focus: string[]; focusAll: string[]; drills: string; homework: string; common: string;
  players: Record<string, PlayerDraft>;
}
interface SheetPlayer { bookingId: string; clientId: string; name: string; inBot: boolean; bookedAt?: string; saved: PlayerDraft }
interface SheetData {
  session: { id: string; startsAt: string; endsAt: string; title: string };
  players: SheetPlayer[];
  saved: { focus: string[]; drills: string; homework: string; common: string };
  draft: Draft | null; draftAt: string | null; publishAt: string | null;
}
interface Axis { id: string; name: string; name_ru?: string | null }

type WordKey = 'co.rate.w0' | 'co.rate.w1' | 'co.rate.w3' | 'co.rate.w5' | 'co.rate.w7' | 'co.rate.w9';
const word = (n: number): WordKey => (n <= 0 ? 'co.rate.w0' : n <= 2 ? 'co.rate.w1' : n <= 4 ? 'co.rate.w3' : n <= 6 ? 'co.rate.w5' : n <= 8 ? 'co.rate.w7' : 'co.rate.w9');

export function GroupNotesSheet({ sessionId, startTab, live, hub, onClose, onDone }: {
  sessionId: string;
  /** The player whose row opens first (the one tapped on the Calendar). */
  startTab?: string;
  /** Opened from the Today card: the lesson's header on top. */
  live?: boolean;
  /** «Панель занятия» under the header: attendance, payment, quick buttons. */
  hub?: ReactNode;
  onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [data, setData] = useState<SheetData | null>(null);
  const [axes, setAxes] = useState<Axis[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [open, setOpen] = useState<string | null>(startTab ?? null);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // «Убрать из занятия» from a player's row.
  const [removing, setRemoving] = useState<SheetPlayer | null>(null);
  /** Each player's last score per skill, by booking id, for «в прошлый раз N». */
  const [last, setLast] = useState<Record<string, Record<string, number>>>({});
  const [restored, setRestored] = useState(false);
  const lastSent = useRef('');
  const pending = useRef<string | null>(null);
  const inFlight = useRef<Promise<void>>(Promise.resolve());
  const closed = useRef(false);

  useEffect(() => {
    Promise.all([
      api<SheetData>(`/api/coach/sessions/${sessionId}/notes`),
      // The session's own sport's skills (tennis or padel).
      api<Axis[]>(`/api/rating-axes?session=${sessionId}`).catch(() => []),
    ]).then(([d, ax]) => {
      setAxes(ax);
      const names = new Set(ax.flatMap((a) => [a.name, a.name_ru].filter(Boolean) as string[]));
      // Changes that never reached the server (Telegram killed offline)
      // are newer than its draft: they come back, and are sent at once.
      const kept = readLocal(browserStore(), groupKey(sessionId), Date.now());
      const local = restoreFrom(kept, d.draftAt);
      if (kept && !local) dropLocal(browserStore(), groupKey(sessionId));
      let fromLocal: Draft | null = null;
      try {
        const l = local ? JSON.parse(local) : null;
        if (l && typeof l.players === 'object' && Array.isArray(l.focus)) {
          const { focusAll: _, ...rest } = l;
          // Only players still in the session.
          fromLocal = { ...rest, players: Object.fromEntries(d.players.filter((p) => l.players[p.bookingId]).map((p) => [p.bookingId, l.players[p.bookingId]])) };
        }
      } catch { /* unreadable: the server's draft stands */ }
      if (fromLocal) { lastSent.current = '-'; setRestored(true); }
      const start: Draft = fromLocal ?? d.draft ?? {
        focus: d.saved.focus.filter((f) => !names.has(f)), focusAll: d.saved.focus,
        drills: d.saved.drills, homework: d.saved.homework, common: d.saved.common,
        players: Object.fromEntries(d.players.map((p) => [p.bookingId, p.saved])),
      };
      for (const p of d.players) start.players[p.bookingId] ??= p.saved;
      // Homework is per player here: a shared one saved before goes to each.
      if (start.homework.trim()) {
        for (const p of d.players) {
          const pd = start.players[p.bookingId];
          if (!pd.homework.trim()) start.players[p.bookingId] = { ...pd, homework: start.homework };
        }
        start.homework = '';
      }
      setDraft(start);
      setData(d);
      if (d.draft) { setState('saved'); setSavedAt(d.draftAt); }
      setOpen((cur) => (cur && d.players.some((p) => p.bookingId === cur) ? cur : d.players[0]?.bookingId ?? null));
      Promise.all(d.players.map((p) => api<{ axis_id: string; score: number }[]>(`/api/coach/clients/${p.clientId}/last-scores`)
        .then((rows) => [p.bookingId, lastScores(rows)] as const).catch(() => [p.bookingId, {}] as const)))
        .then((rows) => setLast(Object.fromEntries(rows)));
    }).catch(async (err) => { await alert(t('co.failed', { message: err.message })); onClose(); });
  }, [sessionId]);

  /** What it was about: the skills anyone was rated on, then the coach's own extras saved before. */
  const focusAll = useMemo(() => {
    if (!draft) return [];
    const rated = axes.filter((a) => Object.values(draft.players).some((p) => (p.scores[a.id] ?? 0) > 0)).map((a) => axisName(a));
    return [...new Set([...rated, ...draft.focus])].slice(0, 6);
  }, [draft, axes]);

  const full = draft ? { ...draft, focusAll } : null;
  const json = full ? JSON.stringify(full) : '';

  const flush = useRef((_k?: boolean) => Promise.resolve());
  flush.current = (keepalive = false) => {
    inFlight.current = inFlight.current.then(async () => {
      const body = pending.current;
      if (!body || closed.current) return;
      pending.current = null;
      try {
        const r = await api<{ draftAt: string }>(`/api/coach/sessions/${sessionId}/notes/draft`, {
          method: 'PUT', body: `{"draft":${body}}`, keepalive,
        });
        lastSent.current = body;
        // On the server now: the browser's copy is no longer needed.
        if (readLocal(browserStore(), groupKey(sessionId), Date.now())?.json === body) dropLocal(browserStore(), groupKey(sessionId));
        setState('saved'); setSavedAt(r.draftAt);
      } catch {
        if (pending.current === null) pending.current = body;
        setState('idle');
      }
    });
    return inFlight.current;
  };

  useEffect(() => {
    if (!data || !json) return;
    if (!lastSent.current) { lastSent.current = json; return; }
    if (json === lastSent.current) { pending.current = null; return; }
    pending.current = json;
    keepLocal(browserStore(), groupKey(sessionId), json, Date.now());
    setState('saving');
    const timer = setTimeout(() => void flush.current(), 600);
    return () => clearTimeout(timer);
  }, [json, data]);

  useEffect(() => {
    const away = () => { if (document.visibilityState === 'hidden') void flush.current(true); };
    const hide = () => void flush.current(true);
    document.addEventListener('visibilitychange', away);
    window.addEventListener('pagehide', hide);
    return () => {
      document.removeEventListener('visibilitychange', away);
      window.removeEventListener('pagehide', hide);
      void flush.current(true);
    };
  }, []);

  if (!data || !draft || !full) {
    return <PinnedSheet full title={t('co.gn.loading')} onClose={onClose} footer={<span />}><div className="skeleton" /></PinnedSheet>;
  }

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const setPlayer = (id: string, patch: Partial<PlayerDraft>) =>
    setDraft({ ...draft, players: { ...draft.players, [id]: { ...draft.players[id], ...patch } } });
  const names = data.players.map((p) => p.name).join(', ');
  const ended = new Date(data.session.endsAt).getTime() <= Date.now();
  const has = (p: PlayerDraft) => Object.values(p.scores).some((v) => v > 0) || !!p.note.trim() || !!p.homework.trim();

  /** «Сохранить»: everyone or no one; on failure nothing typed is lost. */
  async function save() {
    if (!full) return;
    setBusy(true);
    closed.current = true;
    pending.current = null;
    try {
      await inFlight.current;
      await api(`/api/coach/sessions/${sessionId}/notes/finish`, { method: 'POST', body: JSON.stringify({ draft: full }) });
      dropLocal(browserStore(), groupKey(sessionId));
      haptic.success();
      onDone();
      onClose();
    } catch (err: any) {
      closed.current = false;
      // Kept as a draft, so closing now loses nothing either.
      pending.current = JSON.stringify(full);
      void flush.current();
      haptic.error();
      await alert(err.message === 'player_failed' && err.body?.player
        ? t('co.pn.failedPlayer', { name: err.body.player })
        : t('co.pn.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet full
      title={<>{t('co.gn.title', { time: `${fmtTime(data.session.startsAt)}–${fmtTime(data.session.endsAt)}` })}<small className="gn-names">{names}</small></>}
      onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.rate.later')}</button>
        <button className="savebtn" disabled={busy} onClick={save}>{busy ? t('co.pn.saving') : t('co.pn.save')}</button>
      </>}>
      {/* The names are in the title already: three or more read as one line, as on the Today card. */}
      {live && <LiveHeader startsAt={data.session.startsAt} endsAt={data.session.endsAt}
        who={groupSummary(data.players.length)
          ? t('co.today.group', { n: data.players.length, players: t.word('co.today.players', data.players.length) })
          : names} />}
      {live && hub}
      {restored && <div className="gn-status">{t('co.rate.restored')}</div>}
      {state !== 'idle' && (
        <div className={`gn-status${state === 'saving' ? ' gn-status--saving' : ''}`}>
          {state === 'saving' ? <><span className="gn-spin" />{t('co.gn.saving')}</>
            : savedAt ? <>✓ {t('co.gn.saved', { time: fmtTime(savedAt) })}</> : null}
        </div>
      )}
      {!ended && <div className="gn-banner">🕘 {t('co.gn.stillOn', { time: fmtTime(data.session.endsAt) })}</div>}

      <label className="mf">
        <span className="mf__label"><VisLabel text={t('co.ss.did')} who="player" /></span>
        <GrowArea className="notearea" rows={2} value={draft.drills} placeholder={t('co.rate.drills')}
          onChange={(e) => set({ drills: e.target.value })} />
        <DrillSuggest value={draft.drills} onChange={(v) => set({ drills: v })} />
      </label>
      <label className="mf">
        <span className="mf__label"><VisLabel text={t('co.ss.common')} who="player" /></span>
        <GrowArea className="notearea" rows={2} value={draft.common} placeholder={t('co.ss.commonPh')}
          onChange={(e) => set({ common: e.target.value })} />
      </label>

      <div className="ss-rows">
        {data.players.map((p) => {
          const pd = draft.players[p.bookingId];
          const isOpen = open === p.bookingId;
          return (
            <div key={p.bookingId} className={`ss-row${isOpen ? ' ss-row--open' : ''}`}>
              <button className="ss-row__head" aria-expanded={isOpen}
                onClick={() => { haptic.tap(); setOpen(isOpen ? null : p.bookingId); }}>
                <span className="ss-row__name">{p.name}</span>
                {!isOpen && has(pd) && <span className="ss-row__ok" aria-label="✓">✓</span>}
                <span className="ss-row__chev" aria-hidden="true">›</span>
              </button>
              {isOpen && (
                <div className="ss-row__body">
                  <div className="ratelines">
                    {axes.map((a) => {
                      const v = pd.scores[a.id] ?? 0;
                      return (
                        <div className={`rateline${v === 0 ? ' rateline--off' : ''}`} key={a.id}>
                          <div className="rateline__head">
                            <span className="rateline__name">{axisName(a)}</span>
                            <span className="rateline__word">{t(word(v))}</span>
                            <span className="rateline__num">{v}</span>
                          </div>
                          <input type="range" min={0} max={10} step={1} value={v} aria-label={`${axisName(a)} · ${p.name}`}
                            onChange={(e) => setPlayer(p.bookingId, { scores: { ...pd.scores, [a.id]: Number(e.target.value) } })} />
                          {last[p.bookingId]?.[a.id] !== undefined && (
                            <button className="rateline__last" aria-pressed={v === last[p.bookingId][a.id]}
                              aria-label={`${t('co.rate.lastTimeSet', { n: last[p.bookingId][a.id] })} · ${p.name}`}
                              onClick={() => { haptic.tap(); setPlayer(p.bookingId, { scores: takeLast(pd.scores, a.id, last[p.bookingId][a.id]) }); }}>
                              {t('co.rate.lastTime', { n: last[p.bookingId][a.id] })}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <label className="mf">
                    <span className="mf__label"><VisLabel text={t('co.pn.personal', { name: p.name })} who="player" /></span>
                    <GrowArea className="notearea" rows={2} value={pd.note} placeholder={t('co.pn.personalPh', { name: p.name })}
                      onChange={(e) => setPlayer(p.bookingId, { note: e.target.value })} />
                  </label>
                  <label className="mf">
                    <span className="mf__label"><VisLabel text={t('co.pn.homework')} who="player" /></span>
                    <GrowArea className="notearea" rows={1} value={pd.homework} placeholder={t('co.rate.homework')}
                      onChange={(e) => setPlayer(p.bookingId, { homework: e.target.value })} />
                  </label>
                  <label className="mf">
                    <span className="mf__label"><VisLabel text={t('co.rate.workingOnLabel')} who="player" /></span>
                    <GrowArea className="notearea" rows={1} value={pd.workingOn} placeholder={t('co.rate.workingOn')}
                      onChange={(e) => setPlayer(p.bookingId, { workingOn: e.target.value })} />
                  </label>
                  {!p.inBot && <div className="gn-hint">{t('co.gn.noBot')}</div>}
                  {/* Added by mistake: out again, while nothing of theirs is saved. */}
                  {data.players.length >= 2 && (
                    <button className="sess__link rm__link" onClick={async () => {
                      const st = await fetchSeatState(p.bookingId).catch(async (err) => { await alert(t('co.failed', { message: err.message })); return null; });
                      if (!st) return;
                      const no = removeRefusal(t, p.name, { rated: st.blocked === 'rated', paid: st.blocked === 'paid' });
                      if (no) { haptic.error(); await alert(no); return; }
                      setRemoving(p);
                    }}>{t('co.rm.action')}</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {removing && (
        <RemoveSheet bookingId={removing.bookingId} name={removing.name} startsAt={data.session.startsAt}
          inBot={removing.inBot} bookedAt={removing.bookedAt} onClose={() => setRemoving(null)}
          // Who is in the session changed: back to the Calendar, which reloads.
          onDone={() => { closed.current = true; onDone(); onClose(); }} />
      )}
    </PinnedSheet>
  );
}
